import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';

interface EditAttachmentModalProps {
    show: boolean;
    onHide: () => void;
    attachment: {
        id: number;
        titulo: string;
        descricao?: string;
    } | null;
    onSave: (id: number, data: {
        titulo?: string;
        descricao?: string;
        arquivo?: File;
    }) => Promise<void>;
}

interface UploadedFile {
    file: File;
    size: string;
    name: string;
}

export const EditAttachmentModal: React.FC<EditAttachmentModalProps> = ({
    show,
    onHide,
    attachment,
    onSave
}) => {
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (attachment) {
            setTitulo(attachment.titulo || '');
            setDescricao(attachment.descricao || '');
        }
    }, [attachment]);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setUploadedFile({
                file: files[0],
                size: formatFileSize(files[0].size),
                name: files[0].name
            });
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async () => {
        if (!attachment?.id) return;
        
        try {
            setIsSubmitting(true);
            await onSave(attachment.id, {
                titulo,
                descricao,
                arquivo: uploadedFile?.file
            });
            onHide();
        } catch (error) {
            console.error('Erro ao atualizar anexo:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className="custom-modal modal-padding"
        >
            <Modal.Header closeButton>
                <Modal.Title>Editar Arquivo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Título <span className="text-danger">*</span>
                        </label>
                        <input
                            className="form-control"
                            type="text"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Descrição
                        </label>
                        <textarea
                            className="form-control"
                            rows={4}
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="col-form-label">
                            Novo Arquivo
                        </label>
                        <div className="drag-attach">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept="*/*"
                            />
                            <div className="img-upload">
                                <i className="ti ti-file-broken" />
                                Enviar Arquivo
                            </div>
                        </div>
                    </div>
                    {uploadedFile && (
                        <div className="mb-3">
                            <label className="col-form-label">Arquivo Enviado</label>
                            <div className="upload-file upload-list">
                                <div>
                                    <h6>{uploadedFile.name}</h6>
                                    <p>{uploadedFile.size}</p>
                                </div>
                                <button
                                    onClick={handleRemoveFile}
                                    className="text-danger border-0 bg-transparent"
                                    type="button"
                                >
                                    <i className="ti ti-trash-x" />
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="col-lg-12 text-end modal-btn">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={onHide}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !titulo.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};